import React, { useContext, useState, useEffect, useCallback } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import { usePolling } from '../hooks/usePolling';
import PollingIndicator from './shared/PollingIndicator';
import {
  FileText,
  Download,
  CheckCircle,
  Loader2,
  RefreshCw,
  ExternalLink,
  Package,
  AlertCircle
} from 'lucide-react';

const Step7_LabelGeneration = () => {
  const {
    planId,
    requestLabels,
    fetchLabels,
    labels,
    labelsLoading,
    loading
  } = useContext(FBAInboundContext);

  const [step, setStep] = useState(labels.length > 0 ? 'complete' : 'request');
  const [isRequesting, setIsRequesting] = useState(false);

  // Polling for labels
  const {
    status: pollStatus,
    elapsedTime,
    pollCount,
    startPolling,
    stopPolling,
    isPolling
  } = usePolling(
    fetchLabels,
    {
      enabled: false,
      initialInterval: 2000,
      maxInterval: 5000,
      intervalThreshold: 30000,
      shouldStop: (result) => {
        const labelData = result?.data || result;
        return Array.isArray(labelData) && labelData.length > 0 &&
               labelData.some(l => l.url || l.download_url);
      },
      onSuccess: (result) => {
        const labelData = result?.data || result;
        if (Array.isArray(labelData) && labelData.length > 0 &&
            labelData.some(l => l.url || l.download_url)) {
          setStep('complete');
        }
      }
    }
  );

  const handleRequestLabels = async () => {
    setIsRequesting(true);
    try {
      await requestLabels();
      setStep('polling');
      startPolling();
    } catch (err) {
      console.error('Failed to request labels:', err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await fetchLabels();
    } catch (err) {
      console.error('Failed to refresh labels:', err);
    }
  };

  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'label.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Request labels view
  if (step === 'request') {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Generate Box Labels
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Request shipping labels for your boxes. Amazon will generate labels
            that you can print and attach to each box.
          </p>
        </div>

        <button
          onClick={handleRequestLabels}
          disabled={isRequesting || loading}
          className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isRequesting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Requesting Labels...
            </>
          ) : (
            <>
              <FileText className="w-5 h-5 mr-2" />
              Request Box Labels
            </>
          )}
        </button>
      </div>
    );
  }

  // Polling view
  if (step === 'polling') {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Generating Labels
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Amazon is generating your box labels. This may take a few moments.
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <PollingIndicator
            status={pollStatus}
            elapsedTime={elapsedTime}
            pollCount={pollCount}
            message="Waiting for labels to be ready..."
          />
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-500">
            {elapsedTime < 30000
              ? 'Checking every 2 seconds...'
              : 'Checking every 5 seconds...'}
          </p>
        </div>

        <button
          onClick={stopPolling}
          className="w-full py-2 px-4 text-gray-600 bg-gray-100 font-medium rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
    );
  }

  // Complete view with labels
  return (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-green-900">Labels Ready!</h4>
            <p className="text-sm text-green-700 mt-1">
              Your box labels have been generated. Download and print them to attach to your boxes.
            </p>
          </div>
        </div>
      </div>

      {/* Labels List */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-medium text-gray-900">
            Available Labels ({labels.length})
          </h3>
          <button
            onClick={handleRefresh}
            disabled={labelsLoading}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${labelsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="divide-y divide-gray-200">
          {labels.length > 0 ? (
            labels.map((label, index) => (
              <div key={index} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg mr-4">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {label.shipment_id || label.name || `Label ${index + 1}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        {label.label_type || 'Box Label'} •{' '}
                        {label.format || 'PDF'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {(label.url || label.download_url) ? (
                      <>
                        <button
                          onClick={() => handleDownload(
                            label.url || label.download_url,
                            `${label.shipment_id || 'label'}_${index + 1}.pdf`
                          )}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </button>
                        <a
                          href={label.url || label.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-blue-600"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">
                        Generating...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No labels available yet.</p>
              <button
                onClick={handleRefresh}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                Refresh Labels
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Download All */}
      {labels.length > 1 && (
        <div className="text-center pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-3">
            Need all labels in one file?
          </p>
          <button
            onClick={() => {
              // Download each label
              labels.forEach((label, idx) => {
                if (label.url || label.download_url) {
                  setTimeout(() => {
                    handleDownload(
                      label.url || label.download_url,
                      `label_${idx + 1}.pdf`
                    );
                  }, idx * 500);
                }
              });
            }}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            <Download className="w-4 h-4 mr-2" />
            Download All Labels
          </button>
        </div>
      )}

      {/* Completion Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="flex items-start">
          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-900">Workflow Complete!</h4>
            <p className="text-sm text-blue-700 mt-1">
              You have successfully completed all steps of the FBA Inbound workflow.
              Print your labels and ship your boxes to Amazon's fulfillment centers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step7_LabelGeneration;
